import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { usePopupLogin } from '../../hooks/usePopupLogin';
import { 
  CheckCircle, 
  XCircle, 
  ChevronRight, 
  Shield, 
  Building2, 
  User, 
  Briefcase,
  Sparkles,
  Users,
  ArrowRight,
  PartyPopper,
  Check,
  Calendar,
  Mail
} from 'lucide-react';

import { inviteService } from '@/services/inviteService';
import { CompanyUserInvite } from '@/common/types/invitation';
import { toastUtils } from '@/common/utils/toastUtils';
import NotFound from '../NotFound';
import { ValidationErrors } from './types';
import { extractDate, extractDateTime } from '@/common/utils/dateFormat';
import { useNavigationStore } from '@/common/store';
import InisisAuthButton, { InisisAuthResult } from '@/components-common/inisis/InisisAuthButton';
import { commonService } from '@/services/commonService';
import { authService } from '@/services/authService';
import { semanticColors } from '@/common/styles/colorTokens';
import { LabelInput } from '@/common/components/input/LabelInput';
import { getBirthGender, validatePassword } from '@/common/utils/commonUtil';
import { typography } from '@/common/styles/typographyTokens';
import Avatar from '@/common/components/avatar/Avatar';

enum InviteStep {
  INITIAL,
  SIGNUP,
  COMPLETE,
  REJECTED
}

const InvitationCompanyUserPage: React.FC = () => {
  const {  } = useNavigationStore();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [isPageNotFound, setIsPageNotFound] = useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState<InviteStep>(InviteStep.INITIAL);
  const [inviteInfo, setInviteInfo] = useState<CompanyUserInvite | null>(null);
  const [notificationId, setNotificationId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isNewRegistration, setIsNewRegistration] = useState<boolean>(false); // 신규 회원가입 여부

  const [differentUser, setDifferentUser] = useState<boolean>(false);
  const [rejectReason, setRejectReason] = useState<string>('');
  
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [isCertifiedPersonal, setIsCertifiedPersonal] = useState<boolean>(false);

  const [formData, setFormData] = useState({
    loginId: '',
    birthGender: '',
    name: '',
    phone: '',
    email: '',
    password: '',
    passwordConfirm: '',
    userDi: '',
    userCi: '',
    token: '',
  });
  
  const { openPopup, error } = usePopupLogin({
    onSuccess: async (data) => {
      setIsLoading(true);
      try {
        const responseUser = await authService.setUserProfile();
        
        if (!responseUser.success || !responseUser.data) {
          toastUtils.error('사용자 정보를 가져오는 데 실패했습니다.');
          return;
        }
        
        if (!inviteInfo) {
          toastUtils.error('초대 정보를 가져오는 데 실패했습니다.');
          return;
        }

        const isNameMatch = responseUser.data.name === inviteInfo.content?.name;        

        console.log('loginUser', responseUser);

        if (!isNameMatch) {
          toastUtils.error('초대된 사용자와 일치하지 않습니다.');
          setDifferentUser(true);
          return;
        }

        const registeCompanyUserResult = await inviteService.acceptInviteForLogin(notificationId!);
        if (registeCompanyUserResult?.success) {
          toastUtils.success('등록 완료');
          setIsNewRegistration(false); // 기존 로그인으로 완료
          getInviteInfo();
        } else {
          toastUtils.error('등록 실패');
        }
      } catch (error) {
        console.error('로그인 처리 중 오류 발생:', error);
        toastUtils.error('로그인 처리 중 오류가 발생했습니다.');
      } finally {
        setIsLoading(false);
      }
    },
    onError: (err) => {
      console.error('로그인 실패:', err);
    }
  });

  const handleLogin = () => {
    navigate('/login');
  };

  const handleComplete = async () => {
    if (!isCertifiedPersonal) {
      toastUtils.error('간편 인증을 진행해주세요.');
      return;
    }

    setIsLoading(true);
    try {
      const response = await inviteService.acceptInviteForRegister({
        notificationId: notificationId!,
        loginId: formData.loginId,
        residentNumber: formData.birthGender + '******',
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        password: formData.password,
        emailVerified: true,
        userDi: formData.userDi,
        userCi: formData.userCi,
        token: formData.token,
      });
      if (response?.success) {
        toastUtils.success('등록 완료');
        setIsNewRegistration(true); // 신규 회원가입으로 완료
        getInviteInfo();
      } else {
        toastUtils.error('등록 실패');
      }
    } catch (error) {
      console.error('등록 처리 중 오류:', error);
      toastUtils.error('등록 처리 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCertify = async (data: InisisAuthResult) => {
    // 이미 계정이 있는 사용자 → 간편인증 로그인 후 초대 수락 처리
    if (data.isDuplicate) {
      setIsCertifiedPersonal(false);
      setIsLoading(true);
      try {
        // 1. 간편인증 로그인
        const loginResult = await authService.loginWithUserDi(data.userDi, data.userCi, data.token);
        if (!loginResult || !loginResult.success) {
          toastUtils.error(loginResult?.message || '간편인증 로그인에 실패했습니다.');
          return;
        }

        // 2. 유저 프로필 조회
        const responseUser = await authService.setUserProfile();
        if (!responseUser.success || !responseUser.data) {
          toastUtils.error('사용자 정보를 가져오는 데 실패했습니다.');
          return;
        }

        // 3. 초대된 사용자와 일치하는지 검증
        if (!inviteInfo) {
          toastUtils.error('초대 정보를 가져오는 데 실패했습니다.');
          return;
        }

        const isNameMatch = responseUser.data.name === inviteInfo.content?.name;
        if (!isNameMatch) {
          toastUtils.error('초대된 사용자와 일치하지 않습니다.');
          setDifferentUser(true);
          return;
        }

        // 4. 초대 수락 처리
        const acceptResult = await inviteService.acceptInviteForLogin(notificationId!);
        if (acceptResult?.success) {
          toastUtils.success('등록 완료');
          setIsNewRegistration(false);
          getInviteInfo();
        } else {
          toastUtils.error('등록 실패');
        }
      } catch (error) {
        console.error('간편인증 로그인 처리 중 오류 발생:', error);
        toastUtils.error('로그인 처리 중 오류가 발생했습니다.');
      } finally {
        setIsLoading(false);
      }
      return;
    }
    
    // 신규 사용자 → 회원가입 흐름
    if (inviteInfo?.content?.name === data.userName) {
      toastUtils.success(`간편인증이 완료 되었습니다.`);
      setCurrentStep(InviteStep.SIGNUP);
      setIsCertifiedPersonal(true);
      setFormData(prev => ({
        ...prev, 
        birthGender: getBirthGender(data.userBirth, data.userGender), 
        name: data.userName, 
        phone: data.userPhone,  
        userDi: data.userDi, 
        userCi: data.userCi,
        token: data.token
      }));
    } else {
      toastUtils.error('초대자 정보와 일치하지 않습니다.');
      setDifferentUser(true);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toastUtils.error('거절 사유를 입력해주세요.');
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await inviteService.rejectInvite({
        notificationId: notificationId!,
        reason: rejectReason
      });

      if (response?.success) {
        setDifferentUser(false);
        toastUtils.success('초대가 성공적으로 거절되었습니다.');
        getInviteInfo();
      } else {
        throw new Error('거절 처리 실패');
      }
    } catch (error) {
      console.error('초대 거절 실패:', error);
      toastUtils.error('초대 거절 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (touched[name]) {
      setErrors((prev: any) => ({
        ...prev,
        [name]: validateField(name, value)
      }));
    }
  };

  const handleBlur = async (email: string) => {
    const response = await commonService.checkDuplicateValue('email', email);
    if (response.exists) {
      setErrors(prev => ({ ...prev, email: '이미 가입된 이메일입니다.' }));
    }
    
    if (email && inviteInfo?.content?.email && email === inviteInfo.content.email) {
      toastUtils.info('회사 이메일과 같은 이메일입니다. 개인 이메일을 입력해주세요.');
      setErrors(prev => ({ ...prev, email: '회사 이메일과 다른 개인 이메일을 입력해주세요.' }));
    }
  };

  const validateField = useCallback((name: string, value: string): string => {
    switch (name) {
      case 'loginId':
        if (!value.trim()) return '아이디를 입력해주세요';
        if (value.length < 4) return '아이디는 4자 이상이어야 합니다';
        return '';
      case 'name':
        if (!value.trim()) return '이름을 입력해주세요';
        if (value.length < 2) return '이름은 2자 이상이어야 합니다';
        return '';
      case 'phone':
        if (!value) return '휴대폰 번호를 입력해주세요';
        const phoneRegex = /^01([0|1|6|7|8|9])-?([0-9]{3,4})-?([0-9]{4})$/;
        if (!phoneRegex.test(value)) return '올바른 휴대폰 번호 형식이 아닙니다';
        return '';
      case 'email':
        if (!value) return '이메일을 입력해주세요';
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) return '올바른 이메일 형식이 아닙니다';
        return '';
      case 'password':
        if (!value) return '비밀번호를 입력해주세요';
        return validatePassword(value) || '';
      case 'passwordConfirm':
        if (!value) return '비밀번호 확인을 입력해주세요';
        if (value !== formData.password) return '비밀번호가 일치하지 않습니다';
        return '';
      default:
        return '';
    }
  }, [formData.password]);

  const isFormValid = useMemo(() => {
    return isCertifiedPersonal && 
           formData.loginId && 
           formData.email && 
           formData.password && 
           formData.passwordConfirm &&
           formData.password === formData.passwordConfirm &&
           !Object.values(errors).some(error => error !== '');
  }, [isCertifiedPersonal, formData, errors]);

  const getInviteInfo = async () => {
    const nId = searchParams.get('n_id');
    if (!nId) {
      setIsPageNotFound(true);
      return;
    }
    setNotificationId(nId);
    const response = await inviteService.getInviteInfoForCompanyUser(nId);

    if (response) {
      setInviteInfo(response);
      if (response.isApproved === true) {
        setCurrentStep(InviteStep.COMPLETE);
      } else if (response.isApproved === false) {
        setCurrentStep(InviteStep.REJECTED);
      }
    } else {
      setIsPageNotFound(true);
    }    
  };

  useEffect(() => {
    
    getInviteInfo();
  }, []);

  /**
   * 초기 화면 (초대 확인)
   */
  const renderInitialStep = () => (
    <WelcomeContainer>
      <WelcomeContent>
        <WelcomeTextSection>
          <WelcomeInvestorBadge>
            <Avatar alt={inviteInfo?.inviter.companyName || ''} isUser={false} sizes="md" />
            <span>{inviteInfo?.inviter.companyName}</span>
          </WelcomeInvestorBadge>
          
          <WelcomeTitle>
            {inviteInfo?.content?.name}님,<br />
            <WelcomeWave>👋</WelcomeWave> 환영합니다!
          </WelcomeTitle>
          
          <WelcomeDescription>
            <strong>{inviteInfo?.inviter.name}</strong>님이 귀하를 
            <WelcomeHighlight> {inviteInfo?.inviter.companyName}의 팀원</WelcomeHighlight>으로 초대했습니다.
            <br />계속 진행하시려면 간편 인증을 진행하시거나 기존 계정으로 로그인해주세요.
          </WelcomeDescription>

          <InviteInfoPreview>
            <InviteInfoPreviewItem>
              <Briefcase size={16} />
              <span>{inviteInfo?.content?.position || '직책 미정'}</span>
            </InviteInfoPreviewItem>
            <InviteInfoPreviewItem>
              <Users size={16} />
              <span>{inviteInfo?.content?.departments?.map(d => d.name).join(', ') || '부서 미정'}</span>
            </InviteInfoPreviewItem>
            <InviteInfoPreviewItem>
              <Calendar size={16} />
              <span>입사일: {inviteInfo?.content?.joinedAt ? extractDate(inviteInfo.content.joinedAt) : '미정'}</span>
            </InviteInfoPreviewItem>
          </InviteInfoPreview>
          
          {differentUser ? (
            <DifferentUserSection>
              <AlertBox>
                <XCircle size={20} />
                <AlertText>
                  초대된 사용자와 다른 사용자입니다. 자동적으로 거절 처리됩니다.
                  <br />
                  <small>* 사유는 초대자에게 전달됩니다.</small>
                </AlertText>
              </AlertBox>
              <RejectTextarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="거절 사유를 입력해주세요"
              />
              <ButtonRow>
                <BackButton onClick={() => setDifferentUser(false)}>
                  다시하기
                </BackButton>
                <PrimaryButton 
                  onClick={handleReject}
                  disabled={!rejectReason.trim() || isLoading}
                >
                  {isLoading ? '처리 중...' : '거절하기'}
                </PrimaryButton>
              </ButtonRow>
            </DifferentUserSection>
          ) : (
            <WelcomeButtonGroup>
              <InisisAuthButton 
                $variant='fill'
                onSuccess={(data) => handleCertify(data)}
                $title='간편 인증으로 시작하기'
                $size='md'
              />
              <SecondaryButton onClick={openPopup} disabled={isLoading}>
                {isLoading ? '처리 중...' : '기존 계정으로 로그인'}
              </SecondaryButton>
            </WelcomeButtonGroup>
          )}
        </WelcomeTextSection>
        
        <WelcomeImageSection>
          <WelcomeIllustration>
            <WelcomeGlowOrb $delay={0} />
            <WelcomeGlowOrb $delay={2} />
            <WelcomeGlowOrb $delay={4} />
            <WelcomeIconGrid>
              <WelcomeFloatingIcon $delay={0}><Users size={32} /></WelcomeFloatingIcon>
              <WelcomeFloatingIcon $delay={1}><Sparkles size={28} /></WelcomeFloatingIcon>
              <WelcomeFloatingIcon $delay={2}><Building2 size={30} /></WelcomeFloatingIcon>
            </WelcomeIconGrid>
          </WelcomeIllustration>
        </WelcomeImageSection>
      </WelcomeContent>
    </WelcomeContainer>
  );

  /**
   * 회원가입 정보 입력 화면
   */
  const renderSignupStep = () => (
    <CenteredContainer>
      <ContentWrapper>
        {/* 스텝 인디케이터 */}
        <StepIndicator>
          <StepItem $isActive={true}>
            <StepCircle $isActive={true}><Check size={14} /></StepCircle>
            <StepLabel $isActive={true}>본인 확인</StepLabel>
          </StepItem>
          <StepLine $isActive={true} />
          <StepItem $isActive={true} $isCurrent={true}>
            <StepCircle $isActive={true} $isCurrent={true}>2</StepCircle>
            <StepLabel $isActive={true}>정보 입력</StepLabel>
          </StepItem>
          <StepLine $isActive={false} />
          <StepItem $isActive={false}>
            <StepCircle $isActive={false}>3</StepCircle>
            <StepLabel $isActive={false}>완료</StepLabel>
          </StepItem>
        </StepIndicator>

        <ContentCard>
          <CardHeader>
            <CardIconWrapper>
              <User size={24} />
            </CardIconWrapper>
            <div>
              <CardTitle>{formData.name}님, 반갑습니다! 🎉</CardTitle>
              <CardDescription>
                팩트시트 계정 생성을 위해 추가 정보를 입력해주세요.
              </CardDescription>
            </div>
          </CardHeader>

          <FormSection>
            <FormSectionTitle>
              <Check size={16} />
              <span>인증된 정보</span>
            </FormSectionTitle>
            <VerifiedInfoGrid>
              <VerifiedInfoItem>
                <VerifiedInfoLabel>이름</VerifiedInfoLabel>
                <VerifiedInfoValue>{formData.name}</VerifiedInfoValue>
              </VerifiedInfoItem>
              <VerifiedInfoItem>
                <VerifiedInfoLabel>전화번호</VerifiedInfoLabel>
                <VerifiedInfoValue>{formData.phone}</VerifiedInfoValue>
              </VerifiedInfoItem>
              <VerifiedInfoItem>
                <VerifiedInfoLabel>회사 이메일</VerifiedInfoLabel>
                <VerifiedInfoValue>{inviteInfo?.content?.email}</VerifiedInfoValue>
              </VerifiedInfoItem>
            </VerifiedInfoGrid>
          </FormSection>

          <FormSection>
            <FormSectionTitle>
              <Shield size={16} />
              <span>계정 설정</span>
            </FormSectionTitle>
            
            <FormInputGroup>
              <LabelInput
                label="아이디"
                required
                name="loginId"
                type="text"
                value={formData.loginId}
                onChange={(e) => {
                  handleInputChange(e);
                  setTouched(prev => ({ ...prev, loginId: true }));
                }}
                placeholder="아이디를 입력해주세요"
                error={touched.loginId && !!errors.loginId}
                errorMessage={errors.loginId}
              />

              <LabelInput
                label="개인 이메일"
                required
                name="email"
                type="text"
                value={formData.email}
                onChange={(e) => {
                  handleInputChange(e);
                  setTouched(prev => ({ ...prev, email: true }));
                }}
                onBlur={(e) => handleBlur(e.target.value)}
                placeholder="개인 이메일을 입력해주세요"
                error={touched.email && !!errors.email}
                errorMessage={errors.email}
              />

              <FormGrid>
                <div>
                  <LabelInput
                    label="비밀번호"
                    required
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => {
                      handleInputChange(e);
                      setTouched(prev => ({ ...prev, password: true }));
                    }}
                    placeholder="비밀번호를 입력해주세요"
                    error={touched.password && !!errors.password}
                    errorMessage={errors.password}
                  />
                </div>
                
                <div>
                  <LabelInput
                    label="비밀번호 확인"
                    required
                    name="passwordConfirm"
                    type="password"
                    value={formData.passwordConfirm}
                    onChange={(e) => {
                      handleInputChange(e);
                      setTouched(prev => ({ ...prev, passwordConfirm: true }));
                    }}
                    placeholder="비밀번호를 다시 입력해주세요"
                    error={touched.passwordConfirm && !!errors.passwordConfirm}
                    errorMessage={errors.passwordConfirm}
                  />
                </div>
              </FormGrid>
            </FormInputGroup>
          </FormSection>
        </ContentCard>

        <NavigationButtonGroup>
          <BackButton onClick={() => setCurrentStep(InviteStep.INITIAL)}>
            뒤로 가기
          </BackButton>
          <PrimaryButton 
            onClick={handleComplete}
            disabled={!isFormValid || isLoading}
          >
            {isLoading ? '처리 중...' : '가입 완료'}
            <ChevronRight size={18} />
          </PrimaryButton>
        </NavigationButtonGroup>
      </ContentWrapper>
    </CenteredContainer>
  );

  /**
   * 완료 화면
   */
  const renderCompleteStep = () => {
    // 마이페이지로 이동 (기존 로그인 사용자)
    const handleGoToMyPage = () => {
      navigate('/my-space');
    };

    return (
      <SuccessContainer>
        <SuccessContent>
          <SuccessIconWrapper>
            <PartyPopper size={48} />
          </SuccessIconWrapper>
          
          <SuccessTitle>
            {isNewRegistration ? '회원가입이 완료되었습니다!' : '등록이 완료되었습니다!'}
          </SuccessTitle>
          <SuccessDescription>
            축하합니다! 이제 {inviteInfo?.inviter.companyName}의 팀원으로 함께하게 되었습니다.
          </SuccessDescription>
          
          <SuccessCard>
            <SuccessCardHeader>
              <SuccessCardIcon>
                <Building2 size={24} />
              </SuccessCardIcon>
              <SuccessCardInfo>
                <SuccessCardTitle>{inviteInfo?.inviter.companyName}</SuccessCardTitle>
                <SuccessCardDesc>팀원 등록이 완료되었습니다.</SuccessCardDesc>
              </SuccessCardInfo>
            </SuccessCardHeader>
            
            <SuccessInfoGrid>
              <SuccessInfoItem>
                <SuccessInfoLabel>이름</SuccessInfoLabel>
                <SuccessInfoValue>{inviteInfo?.content?.name}</SuccessInfoValue>
              </SuccessInfoItem>
              <SuccessInfoItem>
                <SuccessInfoLabel>부서</SuccessInfoLabel>
                <SuccessInfoValue>{inviteInfo?.content?.departments?.map(d => d.name).join(', ') || '-'}</SuccessInfoValue>
              </SuccessInfoItem>
              <SuccessInfoItem>
                <SuccessInfoLabel>직책</SuccessInfoLabel>
                <SuccessInfoValue>{inviteInfo?.content?.position || '-'}</SuccessInfoValue>
              </SuccessInfoItem>
              <SuccessInfoItem>
                <SuccessInfoLabel>입사일</SuccessInfoLabel>
                <SuccessInfoValue>{inviteInfo?.content?.joinedAt ? extractDate(inviteInfo.content.joinedAt) : '-'}</SuccessInfoValue>
              </SuccessInfoItem>
            </SuccessInfoGrid>
          </SuccessCard>

          {isNewRegistration ? (
            <>
              <NoticeText>
                로그인 시 회사 이메일({inviteInfo?.content?.email})을 사용해주세요.
              </NoticeText>
              <SuccessActionButton onClick={handleLogin}>
                <span>로그인하기</span>
                <ArrowRight size={18} />
              </SuccessActionButton>
            </>
          ) : (
            <>
              <NoticeText>
                이제 새로운 회사에서 서비스를 이용하실 수 있습니다.
              </NoticeText>
              <SuccessActionButton onClick={handleGoToMyPage}>
                <span>마이페이지로 이동</span>
                <ArrowRight size={18} />
              </SuccessActionButton>
            </>
          )}
        </SuccessContent>
      </SuccessContainer>
    );
  };

  /**
   * 거절 완료 화면
   */
  const renderRejectedStep = () => (
    <RejectedContainer>
      <RejectedContent>
        <RejectedIconWrapper>
          <XCircle size={48} />
        </RejectedIconWrapper>
        
        <RejectedTitle>초대가 거절되었습니다</RejectedTitle>
        <RejectedDescription>
          초대 요청이 거절 처리되었습니다.
        </RejectedDescription>
        
        <RejectedCard>
          <RejectedInfoGrid>
            <RejectedInfoItem>
              <RejectedInfoLabel>이름</RejectedInfoLabel>
              <RejectedInfoValue>{inviteInfo?.content?.name}</RejectedInfoValue>
            </RejectedInfoItem>
            <RejectedInfoItem>
              <RejectedInfoLabel>회사명</RejectedInfoLabel>
              <RejectedInfoValue>{inviteInfo?.inviter.companyName}</RejectedInfoValue>
            </RejectedInfoItem>
            <RejectedInfoItem>
              <RejectedInfoLabel>초대자</RejectedInfoLabel>
              <RejectedInfoValue>{inviteInfo?.inviter.name}</RejectedInfoValue>
            </RejectedInfoItem>
          </RejectedInfoGrid>
        </RejectedCard>

        <NoticeText style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
          거절 사유는 초대자에게 전달되었습니다.
        </NoticeText>

        <RejectedActionButton onClick={() => navigate('/')}>
          <span>홈으로 이동</span>
          <ArrowRight size={18} />
        </RejectedActionButton>
      </RejectedContent>
    </RejectedContainer>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case InviteStep.INITIAL:
        return renderInitialStep();
      case InviteStep.SIGNUP:
        return renderSignupStep();
      case InviteStep.COMPLETE:
        return renderCompleteStep();
      case InviteStep.REJECTED:
        return renderRejectedStep();
      default:
        return renderInitialStep();
    }
  };

  if (isPageNotFound) return <NotFound />;
  if (!inviteInfo) return (
    <LoadingContainer>
      <LoadingSpinner>
        <div className="spinner"></div>
        <div>초대 정보를 로드 중입니다...</div>
      </LoadingSpinner>
    </LoadingContainer>
  );

  return (
    <PageContainer>
      {renderCurrentStep()}
    </PageContainer>
  );
};

// ============================================
// Styled Components
// ============================================

const PageContainer = styled.div`
  width: 100vw;
  height: 100vh;
  background-color: ${semanticColors.background.primary};
  font-family: 'Pretendard', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
`;

// 웰컴 화면 스타일
const WelcomeContainer = styled.div`
  width: 100%;
  height: 100%;
  overflow: auto;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(ellipse at 30% 20%, rgba(59, 130, 246, 0.15) 0%, transparent 50%),
                radial-gradient(ellipse at 70% 80%, rgba(16, 185, 129, 0.1) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const WelcomeContent = styled.div`
  width: 100%;
  max-width: 1200px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4rem;
  position: relative;
  z-index: 1;

  @media (max-width: 968px) {
    flex-direction: column;
    text-align: center;
    gap: 3rem;
  }
`;

const WelcomeTextSection = styled.div`
  flex: 1;
  max-width: 580px;

  @media (max-width: 968px) {
    max-width: 100%;
  }
`;

const WelcomeInvestorBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 100px;
  margin-bottom: 1.5rem;
  backdrop-filter: blur(8px);
  
  span {
    ${typography.paragraph.p2.medium}
    color: #fff;
  }
  
  @media (max-width: 968px) {
    margin: 0 auto 1.5rem;
  }
`;

const waveAnimation = keyframes`
  0%, 100% { transform: rotate(0deg); }
  25% { transform: rotate(20deg); }
  75% { transform: rotate(-20deg); }
`;

const WelcomeWave = styled.span`
  display: inline-block;
  animation: ${waveAnimation} 1.5s ease-in-out infinite;
  transform-origin: 70% 70%;
`;

const WelcomeTitle = styled.h1`
  font-size: 3rem;
  font-weight: 800;
  color: #fff;
  margin-bottom: 1.5rem;
  line-height: 1.3;
  letter-spacing: -0.02em;

  @media (max-width: 768px) {
    font-size: 2.25rem;
  }
`;

const WelcomeHighlight = styled.span`
  background: linear-gradient(135deg, #60a5fa 0%, #34d399 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 700;
`;

const WelcomeDescription = styled.p`
  ${typography.paragraph.p1.default}
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 1.5rem;
  line-height: 1.7;

  strong {
    color: #fff;
  }
`;

const InviteInfoPreview = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 2rem;
  
  @media (max-width: 968px) {
    justify-content: center;
  }
`;

const InviteInfoPreviewItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.8);
  ${typography.caption.caption1.medium}
`;

const WelcomeButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  
  @media (max-width: 968px) {
    align-items: center;
  }
`;

const SecondaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.875rem 1.5rem;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 10px;
  color: rgba(255, 255, 255, 0.8);
  ${typography.paragraph.p1.medium}
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.5);
    color: #fff;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// 플로팅 애니메이션
const floatAnimation = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
`;

const glowPulse = keyframes`
  0%, 100% { opacity: 0.3; transform: scale(1); }
  50% { opacity: 0.6; transform: scale(1.1); }
`;

const WelcomeImageSection = styled.div`
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  
  @media (max-width: 968px) {
    min-height: 300px;
    width: 100%;
  }
`;

const WelcomeIllustration = styled.div`
  position: relative;
  width: 100%;
  max-width: 450px;
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const WelcomeGlowOrb = styled.div<{ $delay: number }>`
  position: absolute;
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(59, 130, 246, 0.4) 0%, transparent 70%);
  animation: ${glowPulse} 4s ease-in-out infinite;
  animation-delay: ${props => props.$delay}s;
  
  &:nth-child(1) {
    top: 10%;
    left: 10%;
    background: radial-gradient(circle, rgba(59, 130, 246, 0.3) 0%, transparent 70%);
  }
  
  &:nth-child(2) {
    bottom: 20%;
    right: 10%;
    width: 150px;
    height: 150px;
    background: radial-gradient(circle, rgba(139, 92, 246, 0.3) 0%, transparent 70%);
  }
  
  &:nth-child(3) {
    top: 40%;
    right: 30%;
    width: 100px;
    height: 100px;
    background: radial-gradient(circle, rgba(16, 185, 129, 0.3) 0%, transparent 70%);
  }
`;

const WelcomeIconGrid = styled.div`
  display: flex;
  gap: 2rem;
  position: relative;
  z-index: 1;
`;

const WelcomeFloatingIcon = styled.div<{ $delay: number }>`
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  backdrop-filter: blur(8px);
  color: #fff;
  animation: ${floatAnimation} 3s ease-in-out infinite;
  animation-delay: ${props => props.$delay * 0.5}s;
  
  &:nth-child(2) {
    margin-top: 40px;
    background: rgba(59, 130, 246, 0.2);
    border-color: rgba(59, 130, 246, 0.3);
  }
  
  &:nth-child(3) {
    margin-top: -20px;
    background: rgba(16, 185, 129, 0.2);
    border-color: rgba(16, 185, 129, 0.3);
  }
`;

// 다른 사용자 섹션
const DifferentUserSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const AlertBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem;
  background: rgba(239, 68, 68, 0.15);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 12px;
  color: #fca5a5;
`;

const AlertText = styled.div`
  flex: 1;
  font-size: 0.875rem;
  line-height: 1.5;
  
  small {
    display: block;
    margin-top: 0.5rem;
    opacity: 0.8;
  }
`;

const RejectTextarea = styled.textarea`
  width: 100%;
  height: 120px;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  color: #fff;
  font-family: inherit;
  font-size: 0.875rem;
  resize: none;
  outline: none;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
  
  &:focus {
    border-color: rgba(255, 255, 255, 0.4);
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 1rem;
`;

// 센터 컨테이너
const CenteredContainer = styled.div`
  width: 100%;
  height: 100%;
  overflow: auto;
  padding: 2rem;
  background: ${semanticColors.background.primary};
`;

const ContentWrapper = styled.div`
  max-width: 600px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

// 스텝 인디케이터
const StepIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
`;

const StepItem = styled.div<{ $isActive?: boolean; $isCurrent?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const StepCircle = styled.div<{ $isActive?: boolean; $isCurrent?: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  ${typography.paragraph.p2.medium}
  transition: all 0.3s ease;
  
  background: ${props => 
    props.$isActive 
      ? semanticColors.background.brand 
      : semanticColors.background.secondary};
  color: ${props => 
    props.$isActive 
      ? '#fff' 
      : semanticColors.text.tertiary};
  border: 2px solid ${props => 
    props.$isActive 
      ? semanticColors.background.brand 
      : semanticColors.border.primary};
  
  ${props => props.$isCurrent && `
    box-shadow: 0 0 0 4px ${semanticColors.background.brand}20;
  `}
`;

const StepLabel = styled.span<{ $isActive?: boolean }>`
  ${typography.caption.caption1.default}
  color: ${props => props.$isActive ? semanticColors.text.primary : semanticColors.text.tertiary};
  white-space: nowrap;
  
  @media (max-width: 640px) {
    display: none;
  }
`;

const StepLine = styled.div<{ $isActive?: boolean }>`
  width: 60px;
  height: 2px;
  margin: 0 0.75rem;
  background: ${props => 
    props.$isActive 
      ? semanticColors.background.brand 
      : semanticColors.border.primary};
  transition: background 0.3s ease;
  
  @media (max-width: 640px) {
    width: 30px;
  }
`;

// 콘텐츠 카드
const ContentCard = styled.div`
  background: ${semanticColors.background.secondary};
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    border-radius: 12px;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid ${semanticColors.border.tertiary};
`;

const CardIconWrapper = styled.div`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${semanticColors.background.brand}10;
  border-radius: 12px;
  color: ${semanticColors.background.brand};
  flex-shrink: 0;
`;

const CardTitle = styled.h2`
  ${typography.heading.h5}
  color: ${semanticColors.text.primary};
  margin: 0 0 0.25rem 0;
`;

const CardDescription = styled.p`
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.secondary};
  margin: 0;
`;

// 폼 섹션
const FormSection = styled.div`
  background: ${semanticColors.background.primary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 12px;
  padding: 1.25rem;
  margin-bottom: 1rem;
`;

const FormSectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  ${typography.subtitle.subtitle2}
  color: ${semanticColors.text.primary};
  margin-bottom: 1rem;
  
  svg {
    color: ${semanticColors.background.brand};
  }
`;

const VerifiedInfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const VerifiedInfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const VerifiedInfoLabel = styled.span`
  ${typography.caption.caption1.default}
  color: ${semanticColors.text.tertiary};
`;

const VerifiedInfoValue = styled.span`
  ${typography.paragraph.p2.medium}
  color: ${semanticColors.text.primary};
`;

const FormInputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

// 네비게이션 버튼 그룹
const NavigationButtonGroup = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 0.5rem;
  
  @media (max-width: 640px) {
    flex-direction: column-reverse;
  }
`;

const BackButton = styled.button`
  padding: 0.75rem 1.5rem;
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 10px;
  background: transparent;
  color: ${semanticColors.text.white};
  ${typography.paragraph.p1.medium}
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    color: ${semanticColors.text.secondary};
    background: ${semanticColors.background.secondary};
    border-color: ${semanticColors.border.secondary};
  }
`;

const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: ${semanticColors.background.brand};
  border: none;
  border-radius: 10px;
  color: #fff;
  ${typography.paragraph.p1.medium}
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    opacity: 0.9;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// 로딩 컴포넌트
const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background: ${semanticColors.background.primary};
`;

const LoadingSpinner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  color: ${semanticColors.text.primary};
  
  .spinner {
    width: 40px;
    height: 40px;
    border: 4px solid ${semanticColors.border.primary};
    border-radius: 50%;
    border-top-color: ${semanticColors.background.brand};
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

// 성공 화면 스타일
const SuccessContainer = styled.div`
  width: 100%;
  height: 100%;
  overflow: auto;
  background: linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #1e1b4b 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(ellipse at 50% 30%, rgba(139, 92, 246, 0.2) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const SuccessContent = styled.div`
  max-width: 500px;
  width: 100%;
  text-align: center;
  position: relative;
  z-index: 1;
`;

const successIconPulse = keyframes`
  0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(139, 92, 246, 0.4); }
  50% { transform: scale(1.05); box-shadow: 0 0 0 20px rgba(139, 92, 246, 0); }
`;

const SuccessIconWrapper = styled.div`
  width: 100px;
  height: 100px;
  margin: 0 auto 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%);
  border-radius: 50%;
  color: #fff;
  animation: ${successIconPulse} 2s ease-in-out infinite;
`;

const SuccessTitle = styled.h1`
  ${typography.heading.h3}
  color: #fff;
  margin-bottom: 0.75rem;
`;

const SuccessDescription = styled.p`
  ${typography.paragraph.p1.default}
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 2rem;
`;

const SuccessCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  backdrop-filter: blur(8px);
`;

const SuccessCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const SuccessCardIcon = styled.div`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(139, 92, 246, 0.3);
  border-radius: 12px;
  color: #c4b5fd;
`;

const SuccessCardInfo = styled.div`
  text-align: left;
`;

const SuccessCardTitle = styled.h3`
  ${typography.subtitle.subtitle1}
  color: #fff;
  margin: 0;
`;

const SuccessCardDesc = styled.p`
  ${typography.caption.caption1.default}
  color: rgba(255, 255, 255, 0.6);
  margin: 0;
`;

const SuccessInfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const SuccessInfoItem = styled.div`
  text-align: left;
`;

const SuccessInfoLabel = styled.div`
  ${typography.caption.caption1.default}
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 0.25rem;
`;

const SuccessInfoValue = styled.div`
  ${typography.paragraph.p2.medium}
  color: #fff;
`;

const NoticeText = styled.p`
  ${typography.caption.caption1.default}
  color: ${semanticColors.text.white};
  margin-bottom: 1.5rem;
`;

const SuccessActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 2rem;
  background: #fff;
  border: none;
  border-radius: 12px;
  color: #1e1b4b;
  ${typography.paragraph.p1.medium}
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(255, 255, 255, 0.2);
  }
`;

// 거절 화면 스타일
const RejectedContainer = styled.div`
  width: 100%;
  height: 100%;
  overflow: auto;
  background: linear-gradient(135deg, #450a0a 0%, #7f1d1d 50%, #450a0a 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(ellipse at 50% 30%, rgba(239, 68, 68, 0.2) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const RejectedContent = styled.div`
  max-width: 500px;
  width: 100%;
  text-align: center;
  position: relative;
  z-index: 1;
`;

const rejectedIconPulse = keyframes`
  0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4); }
  50% { transform: scale(1.05); box-shadow: 0 0 0 20px rgba(239, 68, 68, 0); }
`;

const RejectedIconWrapper = styled.div`
  width: 100px;
  height: 100px;
  margin: 0 auto 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #ef4444 0%, #f87171 100%);
  border-radius: 50%;
  color: #fff;
  animation: ${rejectedIconPulse} 2s ease-in-out infinite;
`;

const RejectedTitle = styled.h1`
  ${typography.heading.h3}
  color: #fff;
  margin-bottom: 0.75rem;
`;

const RejectedDescription = styled.p`
  ${typography.paragraph.p1.default}
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 2rem;
`;

const RejectedCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  backdrop-filter: blur(8px);
`;

const RejectedInfoGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const RejectedInfoItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  
  &:last-child {
    padding-bottom: 0;
    border-bottom: none;
  }
`;

const RejectedInfoLabel = styled.div`
  ${typography.caption.caption1.default}
  color: rgba(255, 255, 255, 0.5);
`;

const RejectedInfoValue = styled.div`
  ${typography.paragraph.p2.medium}
  color: #fff;
`;

const RejectedActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 2rem;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 12px;
  color: #fff;
  ${typography.paragraph.p1.medium}
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-2px);
  }
`;

export default InvitationCompanyUserPage;
